import React from 'react';

import { patientCasesFiltersReducer } from "../../reducers/patient-cases-reducer";
import { formattedTimeBetweenDates } from "../../services/Date";
import { getDiagnosisCase } from "../../services/getPatientAtribute";

import { useEffect, useReducer, useState } from "react";
import { useTranslation } from "react-i18next";
import useUiPersistanceApi from "../../hooks/useUiPersistanceApi";

import EnvironmentsString from "../../components/Helpers/EnvironmentsString";
import ViewFileLink from "../../components/PatientsPage/ViewFileLink/ViewFileLink";

import moment from "moment";

import { IColumnOrder } from "../../interfaces/UtilInterfaces";
import { CaseHistory } from "../../interfaces/Patient";
import { Column } from "../../interfaces/Columns";
import { UIPersistenceKeys } from "../../interfaces/UserInfo";

const PERSISTANCE_KEY: UIPersistenceKeys = 'bchome_columns_cases_ids';

const useFilterCaseHistory = () => {

    const { t } = useTranslation();

    const DEFAULT_COLUMNS_CASE_HISTORY: Column<CaseHistory>[] = [
        {
            id: 0,
            name: () => t('name'),
            visible: true,
            sortable: true,
            cell: (c) => c.patient_full_name
        },
        {
            id: 1,
            name: () => t('case'),
            visible: true,
            sortable: true,
            cell: (c) => c.hospital_case ?? '-'
        },
        {
            id: 2,
            name: () => t('environment'),
            visible: true,
            sortable: false,
            element: (c) => typeof c.profile_id === 'number' ? <EnvironmentsString profileIDs={[c.profile_id]} /> : <>-</>
        },
        {
            id: 3,
            name: () => t('diagnosis'),
            visible: true,
            sortable: false,
            cell: (c) => getDiagnosisCase(c.diagnoses ?? [])
        },
        {
            id: 4,
            name: () => t('start_date'),
            visible: true,
            sortable: true,
            cell: (c) => moment(c.start_date).format('DD/MM/YYYY')
        },
        {
            id: 5,
            name: () => t('end_date'),
            visible: true,
            sortable: true,
            cell: (c) => c.end_date ? moment(c.end_date).format('DD/MM/YYYY') : '-'
        },
        {
            id: 6,
            name: () => t('ending_type'),
            visible: false,
            sortable: false,
            cell: (c) => c.closure_reason_name ?? '-'
        },
        {
            id: 7,
            name: () => t('stay_time'),
            visible: true,
            sortable: false,
            cell: (c) => {
                const time = formattedTimeBetweenDates(c.start_date, c.end_date);
                if (time === null) return '-';
                const { days, hours, minutes } = time;
                return `${days ? `${days} ${t('days')}${hours ? ', ' : ''}` : ''}${hours ? `${hours} ${t('hours')}${minutes ? ', ' : ''}` : ''}${minutes ? `${minutes} ${t('minutes')}` : ''}`;
            }
        },
        {
            id: 8,
            name: () => '',
            visible: true,
            sortable: false,
            editVisiblilityDisabled: true,
            element: (c) => <ViewFileLink link={`${c.id}`} />
        },
    ];

    const [columns, setColumns] = useState<Column<CaseHistory>[]>(DEFAULT_COLUMNS_CASE_HISTORY);

    const [filters, dispatchFilters] = useReducer(patientCasesFiltersReducer, { searchText: null, entryDateFrom: null, exitDateTo: null, environmentID: null });
    const [currentPage, setCurrentPage] = useState(1);
    const [columnOrder, setColumnOrder] = useState<IColumnOrder>({ asc: false, col: 3 });

    const { get, set } = useUiPersistanceApi();

    useEffect(() => {
        get((data) => {
            const visibleColumns = data.find(d => d.key === PERSISTANCE_KEY)?.value.split(',').map(id => Number(id));
            setColumns((oldColumns) => {
                return oldColumns.map(c => ({
                    ...c,
                    visible: visibleColumns?.includes(c.id) ?? c.visible
                }));
            });
        });
    }, [get]);

    const changeVisibility = (id: number) => {
        setColumns((oldColumns) => {
            const newColumns = oldColumns.map(column => {
                if (column.id !== id) return column;
                const newColumn = { ...column };
                return { ...newColumn, visible: !newColumn.visible };
            });
            set(PERSISTANCE_KEY, newColumns.filter(col => col.visible).map(col => col.id).join());
            return newColumns;
        });
    };

    return {
        filters,
        currentPage,
        columnOrder,
        dispatchFilters,
        setCurrentPage,
        setColumnOrder,
        columns,
        changeVisibility,
    };

};

export default useFilterCaseHistory;